/**
 * خروج از حساب، طوری که همیشه **تمام شود**.
 *
 * ماجرایی که این فایل را ساخت (T25): بعد از حذفِ حساب، اپ همچنان وارد می‌ماند.
 * تابعِ `delete_my_account` (مایگریشن 0046) خودش `auth.sessions` و
 * `auth.refresh_tokens` را پاک می‌کند، پس وقتی نوبت به `signOut()` می‌رسد
 * نشستِ سمتِ سرور دیگر وجود ندارد.
 *
 * ⚠ فرضیه‌ی اولیه — «پس `signOut()` خطا می‌دهد و توکن روی دستگاه می‌ماند» —
 * روی دیتابیسِ لوکال با supabase-js نسخه‌ی ۲.۱۱۰ **بازتولید نشد**: هر دو
 * حالت (توکنِ معتبر، و توکنِ منقضی با refresh_tokenِ پاک‌شده) امتحان شدند و
 * در هر دو، کتابخانه با وجودِ خطای برگشتی خودش کلید را برمی‌داشت و
 * `SIGNED_OUT` را پخش می‌کرد. پس این فایل یک خطِ مشخص را وصله نمی‌کند؛
 * **نتیجه** را تضمین می‌کند، برای همه‌ی راه‌هایی که هنوز باز مانده‌اند:
 * پاسخی که هیچ‌وقت نمی‌آید (شبکه‌ی گوشی)، شکستِ خودِ storage، و رفتارِ
 * نسخه‌های دیگرِ کتابخانه.
 *
 * قاعده‌ی این‌جا یک جمله است: **درخواستِ سرور ممکن است شکست بخورد یا اصلاً
 * جواب ندهد، ولی پاک‌شدنِ نشست از این دستگاه اختیاری نیست.**
 *
 * و یک نکته‌ی جدا برای حسابی که دیگر وجود ندارد (`accountGone`): آن‌جا اصلاً
 * از سرور نمی‌پرسیم. تستِ E2E نشان داد `POST /auth/v1/logout` در آن مسیر
 * ۴۰۳ برمی‌گرداند — طبیعی است، نشست را خودمان چند میلی‌ثانیه قبل کشته‌ایم —
 * و آن ۴۰۳ هم در کنسول می‌نشیند و هم یک راهِ شکستِ بی‌فایده است. پس اول کلید
 * را از storage برمی‌داریم؛ آن‌وقت supabase چیزی برای فرستادن نمی‌بیند و
 * خروج کاملاً محلی و بدونِ شبکه تمام می‌شود.
 *
 * پس ترتیب سه‌گام است:
 *   ۱. خروجِ عادی، ولی با **مهلت**. اگر گرفت، تمام — supabase خودش storage
 *      را پاک و `SIGNED_OUT` را پخش می‌کند.
 *   ۲. اگر نگرفت (خطا داد یا مهلت تمام شد): خطا گزارش می‌شود و کلیدهای نشست
 *      را **خودمان** از storage برمی‌داریم.
 *   ۳. و یک‌بار دیگر `signOut({ scope: 'local' })`. این تکرارِ بی‌خود نیست:
 *      supabase نشست را از storage می‌خواند، حالا خالی می‌بیند، مسیرِ «نشستی
 *      نیست» را می‌رود و همان‌جا `SIGNED_OUT` را پخش می‌کند. بدونِ این گام،
 *      نسخه‌ی درون‌حافظه‌ی کلاینت و هر شنونده‌ی `onAuthStateChange` تا رفرشِ
 *      بعدی فکر می‌کنند هنوز وارد هستیم.
 *
 * چرا مهلت، و چرا فقط در این حالت گامِ سوم برداشته نمی‌شود: supabase-js
 * درخواست‌های auth را پشتِ یک قفل صف می‌کند. اگر درخواستِ اول هنوز در جریان
 * باشد، درخواستِ دوم پشتِ همان قفل می‌ماند و `await`ِ ما هم با آن گیر می‌کند —
 * یعنی همان چیزی که می‌خواستیم از آن فرار کنیم. پس بعد از مهلت فقط storage
 * را خالی می‌کنیم و برمی‌گردیم؛ درخواستِ اول هر وقت جواب گرفت روی storageِ
 * خالی می‌نشیند و بی‌ضرر است. حالتِ صفحه هم منتظرِ آن نمی‌ماند: `session.tsx`
 * خودش بعد از این تابع نشست و پروفایل را خالی می‌کند.
 *
 * هیچ‌کدام از توابعِ این فایل throw نمی‌کنند. صداکننده — صفحه‌ی حذفِ حساب —
 * باید در هر حالتی به صفحه‌ی ورود برود، نه فقط وقتی شبکه همراهی کرد.
 */
import { supabase } from './supabase.ts';
import { reportError } from './report.ts';
import { forgetCity } from './cityScope.ts';
import { storage, supabaseCreds } from '../platform.ts';

/**
 * **چرا** داریم بیرون می‌آییم — نه اینکه با چه scopeی. آن یکی نتیجه‌ی این است.
 *
 * `user`: کاربر خودش «خروج» را زده. حسابش سرِ جایش است، پس از سرور می‌خواهیم
 * نشست‌های همه‌ی دستگاه‌ها را ببندد — **به‌جز** دستگاه‌هایی که «به این دستگاه
 * اعتماد دارم» خورده‌اند (WHOSUP-349، مایگریشن 0188): اول `sign_out_untrusted`
 * بقیه را می‌بندد و بعد همین دستگاه با `scope: 'local'` بیرون می‌آید — که
 * نشستِ خودِ این دستگاه را روی سرور هم می‌بندد (GoTrue سطرِ `auth.sessions`
 * را پاک می‌کند و علامتِ اعتمادش با آبشار می‌رود). فقط اگر آن تابع روی این
 * دیتابیس اصلاً نبود، همان `scope: 'global'`ِ قدیمی — دستگاهِ مورد اعتماد بدونِ
 * آن مایگریشن وجود ندارد که چیزی از دست برود. شکستِ شبکه **سراسری نمی‌شود**
 * (WHOSUP-374).
 *
 * `accountGone`: حساب دیگر وجود ندارد — یا همین حالا حذف شد، یا `me()` دید
 * که `deleted_at` خورده. این‌جا نشستِ سمتِ سرور از قبل مرده و پرسیدن از او
 * فقط یک ۴۰۳ است.
 */
export type SignOutMode = 'user' | 'accountGone';

/** همان چیزی که supabase-js اسمش را scope می‌گذارد. */
type SignOutScope = 'global' | 'local';

/**
 * همان فرمولی که supabase-js برای `storageKey` پیش‌فرض دارد
 * (`sb-<اولین تکه‌ی هاست>-auth-token`).
 *
 * این‌جا تکرار شده تا وقتی کلاینت در دسترس نیست هم جواب داشته باشیم؛ ولی
 * `storedSessionKeys` اول سراغِ خودِ کلاینت می‌رود، تا اگر روزی این فرمول در
 * supabase-js عوض شد، کلیدِ اشتباهی پاک نکنیم.
 */
export function defaultStorageKey(url: string): string {
  let host = 'placeholder';
  try {
    host = new URL(url).hostname;
  } catch {
    // آدرسِ نامعتبر — همان جانشینی که lib/supabase.ts هم می‌گذارد.
  }
  return `sb-${host.split('.')[0]}-auth-token`;
}

/**
 * همه‌ی کلیدهایی که GoTrue زیرِ یک `storageKey` روی دستگاه می‌نویسد.
 * دقیقاً همان سه‌تایی که `_removeSession` خودش پاک می‌کند — نه کمتر: ماندنِ
 * `-code-verifier` یعنی یک جریانِ ورودِ نیمه‌کاره روی دستگاه باقی می‌ماند.
 */
export function storedSessionKeys(base: string): string[] {
  return [base, `${base}-code-verifier`, `${base}-user`];
}

/**
 * بیشتر از این منتظرِ سرور نمی‌مانیم. حساب از قبل حذف شده و کاربر جلوی یک
 * صفحه‌ی «در حال انجام» نشسته؛ چند ثانیه سکوت باید به خروج ختم شود، نه به
 * چرخنده‌ای که تمام نمی‌شود.
 */
export const SIGN_OUT_TIMEOUT_MS = 5000;

/** چیزهایی که این منطق از بیرون لازم دارد — تزریق‌شده، تا تست‌پذیر بماند. */
export interface SignOutIo {
  /** خودِ درخواستِ خروج. خطا را یا برمی‌گرداند یا پرت می‌کند؛ هر دو مهم است. */
  signOut(scope: SignOutScope): Promise<{ error?: unknown } | null | undefined>;
  /**
   * بستنِ نشست‌های دیگرِ همین حساب به‌جز مورد اعتمادها (`sign_out_untrusted`).
   * نباشد یعنی همان رفتارِ قدیمی: یک خروجِ سراسری.
   */
  signOutOthers?(): Promise<{ error?: unknown } | null | undefined>;
  /** برداشتنِ کلیدهای نشست از storageِ همین دستگاه. */
  clearStored(): Promise<void>;
  report(where: string, e: unknown): void;
  /** مهلتِ درخواستِ سرور بر حسبِ میلی‌ثانیه؛ `0` یعنی بی‌مهلت. */
  timeoutMs?: number;
}

/** «جواب نداد» — فرقش با خطا فقط در گامِ سوم است (نگاه کن به بالای فایل). */
const TIMED_OUT = Symbol('sign-out timed out');

/**
 * یک تلاش برای خروج؛ خطا (یا `TIMED_OUT`) را برمی‌گرداند، و `null` اگر گرفت.
 * هرگز throw نمی‌کند و هیچ تایمری هم پشتِ سرش باز نمی‌گذارد.
 */
async function attempt(scope: SignOutScope, io: SignOutIo): Promise<unknown> {
  return timed(() => io.signOut(scope), io);
}

/** همان مهلت و همان قاعده‌ی «throw نکن»، برای هر درخواستی. */
async function timed(
  send: () => Promise<{ error?: unknown } | null | undefined>, io: SignOutIo,
): Promise<unknown> {
  const ms = io.timeoutMs ?? SIGN_OUT_TIMEOUT_MS;
  // خطای درخواستی که پشتِ سر رهایش می‌کنیم نباید به‌شکلِ unhandled rejection
  // بالا بیاید — روی موبایل همان می‌شود یک کرشِ گزارش‌شده‌ی بی‌ربط.
  const call = Promise.resolve().then(send).then(
    (res) => (res && res.error ? res.error : null),
    (e: unknown) => e ?? new Error('sign-out failed'),
  );
  if (ms <= 0) return await call;

  let timer: ReturnType<typeof setTimeout> | undefined;
  const bell = new Promise<typeof TIMED_OUT>((resolve) => {
    timer = setTimeout(() => resolve(TIMED_OUT), ms);
  });
  try {
    return await Promise.race([call, bell]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/** پاک‌سازیِ storage که خودش هم نباید بتواند کارِ خروج را بخواباند. */
async function clearQuietly(io: SignOutIo): Promise<void> {
  try {
    await io.clearStored();
  } catch (e) {
    // اگر خودِ storage هم نشد (حالتِ ناشناسِ مرورگر)، دیگر کاری از دستمان
    // برنمی‌آید — ولی باز هم نباید این‌جا بترکیم.
    io.report('auth.signOut.clear', e);
  }
}

/**
 * «این تابع روی این دیتابیس وجود ندارد» — یعنی مایگریشنِ 0188 هنوز اجرا نشده.
 * `PGRST202` جوابِ PostgREST است و `42883` همان خطا از خودِ پستگرس.
 */
export function isMissingFunction(e: unknown): boolean {
  const code = (e as { code?: unknown } | null)?.code;
  return code === 'PGRST202' || code === '42883';
}

/** منطقِ خالص — نگاه کن به توضیحِ بالای فایل. */
export async function runSignOut(mode: SignOutMode, io: SignOutIo): Promise<void> {
  // حسابی که دیگر نیست: اول کلید را برمی‌داریم، تا supabase چیزی برای فرستادن
  // نبیند و خروج بدونِ آن ۴۰۳ِ محتوم و بدونِ شبکه تمام شود.
  if (mode === 'accountGone') await clearQuietly(io);

  let scope: SignOutScope = mode === 'accountGone' ? 'local' : 'global';
  if (mode === 'user' && io.signOutOthers) {
    const others = await timed(() => io.signOutOthers!(), io);
    // بقیه بسته شدند (به‌جز مورد اعتمادها)؛ حالا فقط همین دستگاه مانده.
    // ⚠ خروجِ سراسری فقط وقتی که آن تابع روی این دیتابیس **اصلاً نیست**
    // (WHOSUP-374). شکستِ شبکه یا مهلت دلیلِ سراسری شدن نیست: درخواستِ دوم
    // ممکن است برسد و همان دستگاه‌های مورد اعتماد را هم ببندد.
    if (!others || !isMissingFunction(others)) scope = 'local';
    if (others) io.report(others === TIMED_OUT ? 'auth.signOut.others.timeout' : 'auth.signOut.others', others);
  }
  const failure = await attempt(scope, io);
  if (!failure) return;

  io.report(failure === TIMED_OUT ? 'auth.signOut.timeout' : 'auth.signOut', failure);
  await clearQuietly(io);

  // درخواستِ اول هنوز در جریان است و قفلِ auth دستِ اوست؛ دومی فقط پشتش
  // می‌ماند. نگاه کن به توضیحِ بالای فایل.
  if (failure === TIMED_OUT) return;

  const retry = await attempt('local', io);
  if (retry) io.report('auth.signOut.retry', retry);
}

/** `storageKey`ِ واقعیِ کلاینت، و اگر در دسترس نبود همان فرمولِ پیش‌فرض. */
function sessionKeyBase(): string {
  const fromClient = (supabase.auth as unknown as { storageKey?: unknown }).storageKey;
  if (typeof fromClient === 'string' && fromClient) return fromClient;
  return defaultStorageKey(supabaseCreds().url);
}

/**
 * خروجِ واقعی: همان منطق، وصل به supabase و storage و reportError.
 *
 * `storage()` روی هر دو پلتفرم دقیقاً همان جاییست که GoTrue می‌نویسد — روی
 * موبایل چون خودمان همین پورت را به کلاینت داده‌ایم (lib/supabase.ts)، و روی
 * وب چون هر دو `window.localStorage`اند.
 */
export async function signOutEverywhere(mode: SignOutMode = 'user'): Promise<void> {
  // حسابِ رفته شهرش را هم روی دستگاه جا نمی‌گذارد (WHOSUP-352) — وگرنه حسابِ
  // بعدی روی همین گوشی آن را در فرمِ نمایه پیش‌پر می‌بیند. **قبل** از خروج،
  // چون `SIGNED_OUT` همان لحظه cityBoot را برای مهمان راه می‌اندازد و حدسِ آن
  // باید حالتِ «فراموش‌شده» را ببیند. خروجِ معمولی (`user`) عمداً دست نمی‌زند:
  // حساب سرِ جایش است و شهرش همان شهرِ این آدم.
  if (mode === 'accountGone') forgetCity();
  await runSignOut(mode, {
    signOut: (s) => supabase.auth.signOut({ scope: s }),
    signOutOthers: async () => await supabase.rpc('sign_out_untrusted'),
    clearStored: async () => {
      for (const key of storedSessionKeys(sessionKeyBase())) {
        await storage().removeItem(key);
      }
    },
    report: reportError,
  });
}
